import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Hotel as VenueIcon } from '../../../assests/ExportImageIcon'
import service from '../../../services/service'
import endpoints from '../../../services/endpoints'
import '../mytrip/mytrips.css'

// Fallback only — real tabs (with live counts) come from the /users/trips
// response itself once loaded, same pattern as mytrips.jsx.
const FALLBACK_TABS = [
    { key: 'upcoming', label: 'Upcoming' },
    { key: 'completed', label: 'Completed' },
    { key: 'cancelled', label: 'Cancelled' },
]

const isCancelled = (booking) => {
    const status = String(booking.status || '').toLowerCase()
    return (
        booking.manage_tab === 'cancelled'
        || status.includes('cancel')
        || status === 'refunded'
        || status === 'failed'
    )
}

// Confirmed against a real service=venue row on /users/trips.
const mapVenueTrip = (trip) => ({
    id: trip.id,
    // Rating API's object_id needs this underlying venue record id, not the booking id.
    serviceId: trip.service_id,
    status: trip.status_label || trip.status || '—',
    cancelled: isCancelled(trip),
    title: trip.title || '—',
    subtitle: trip.subtitle || '—',
    date: trip.date_label || '—',
    location: trip.location || '—',
    guests: trip.passengers || '',
    serviceProvider: trip.service_provider || '',
})

const ArrowRight = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M3 9h11M10 5l4 4-4 4" stroke="#026D4D" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const MyVenues = () => {
    const navigate = useNavigate()
    const [activeTab, setActiveTab] = useState('upcoming')
    const [trips, setTrips] = useState([])
    const [loading, setLoading] = useState(true)
    const [tabsMeta, setTabsMeta] = useState(FALLBACK_TABS)

    useEffect(() => {
        let cancelled = false
        setLoading(true)
        service
            .get(endpoints.users.trips, { tab: activeTab, service: 'venue', limit: 100 }, { silent: true })
            .then(({ data }) => {
                if (cancelled) return
                setTrips((data.data?.trips || []).map(mapVenueTrip))
                if (data.data?.tabs?.length) setTabsMeta(data.data.tabs)
            })
            .catch(() => {
                if (!cancelled) setTrips([])
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => { cancelled = true }
    }, [activeTab])

    const activeTabLabel = tabsMeta.find((tab) => tab.key === activeTab)?.label || 'Venues'

    const handleOpenTrip = (trip) => {
        navigate(`/trip-doc?id=${trip.id}&service=venue`)
    }

    // Same pattern as mytrips.jsx's "Rate" action — lands on the Rating &
    // Reviews tab with the service/booking id locked to this venue booking.
    const handleRateTrip = (trip) => {
        navigate('/profile', {
            state: {
                section: 'rating-reviews',
                prefill: {
                    serviceType: 'venue',
                    objectId: String(trip.serviceId || trip.id),
                    tripName: trip.title !== '—' ? trip.title : '',
                    provider: trip.serviceProvider || 'self',
                },
            },
        })
    }

    return (
        <div className="myTrips">
            <div className="myTripsToolbar">
                <div className="commonBookingTab myTripsTabs">
                    {tabsMeta.map((tab) => (
                        <div
                            key={tab.key}
                            className={`bookTab ${activeTab === tab.key ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            {/* Server tab counts aren't confirmed to be scoped to service=venue —
                                only show a count for the active tab, using what's actually on screen. */}
                            {tab.label}{tab.key === activeTab && !loading ? ` (${trips.length})` : ''}
                        </div>
                    ))}
                </div>
            </div>

            <div className="bsSectionTitle mt-4">{activeTabLabel} Venue Bookings</div>

            {loading && <p className="fare-extra-hint">Loading your venue bookings...</p>}
            {!loading && !trips.length && (
                <p className="fare-extra-hint">No venue bookings here yet.</p>
            )}

            {!loading && trips.length > 0 && (
                <div className="myTripsList">
                    {trips.map((trip) => (
                        <div
                            className="myTripCard"
                            role="button"
                            tabIndex={0}
                            key={trip.id}
                            onClick={() => handleOpenTrip(trip)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleOpenTrip(trip) }}
                        >
                            <div className="myTripCard__top">
                                <span className="myTripCard__id">ID: {trip.id}</span>
                                <span className="myTripCard__topTags">
                                    <span className={`myTripCard__status ${trip.cancelled ? 'myTripCard__status--cancelled' : ''}`}>
                                        {trip.status}
                                    </span>
                                    {activeTab === 'completed' && !trip.cancelled && (
                                        <button
                                            type="button"
                                            className="myTripCard__rateBtn"
                                            onClick={(e) => { e.stopPropagation(); handleRateTrip(trip) }}
                                        >
                                            Rate
                                        </button>
                                    )}
                                </span>
                            </div>
                            <div className="myTripCard__main">
                                <div className="myTripCard__service">
                                    <span className="myTripCard__icon">
                                        <img src={VenueIcon} alt="" />
                                    </span>
                                    <div className="myTripCard__info">
                                        <div className="myTripCard__title">{trip.title}</div>
                                        {trip.subtitle && trip.subtitle !== '—' && (
                                            <div className="myTripCard__code">{trip.subtitle}</div>
                                        )}
                                    </div>
                                </div>
                                <div className="myTripCard__meta myTripCard__meta--date">
                                    <span className="myTripCard__metaLabel">Date:</span>
                                    <span className="myTripCard__metaValue">{trip.date}</span>
                                </div>
                                <div className="myTripCard__meta myTripCard__meta--location">
                                    <span className="myTripCard__metaLabel">Location:</span>
                                    <span className="myTripCard__metaValue">{trip.location}</span>
                                </div>
                                {trip.guests && (
                                    <div className="myTripCard__meta myTripCard__meta--passengers">
                                        <span className="myTripCard__metaLabel">Guests:</span>
                                        <span className="myTripCard__metaValue">{trip.guests}</span>
                                    </div>
                                )}
                                <span className="myTripCard__arrow" aria-hidden="true">
                                    <ArrowRight />
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

export default MyVenues
